
import { Icon, type IconName } from '@/components/ui/Icon'
import { THEME_LABELS, THEMES, type Theme } from '@/lib/theme'
import { cx, segment } from '@/lib/ui'
import { useTheme } from './ThemeProvider'

const ICONS: Record<Theme, IconName> = { light: 'sun', dark: 'moon', system: 'monitor' }

/**
 * Light, dark, or the device's choice. Two shapes: a row of three pills for
 * a header, and a stacked list for the account popover, so the same control
 * fits both the rail and the sign-in pages.
 */
export function ThemeMenu({ variant = 'list' }: { variant?: 'list' | 'segments' }) {
  const { theme, setTheme } = useTheme()

  if (variant === 'segments') {
    return (
      <div role="radiogroup" aria-label="Theme" className="flex items-center gap-0.5 rounded-full bg-hover p-0.5">
        {THEMES.map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={theme === option}
            aria-label={THEME_LABELS[option]}
            title={THEME_LABELS[option]}
            onClick={() => setTheme(option)}
            className={cx(segment.base, 'h-7 w-7 justify-center px-0', theme === option ? segment.on : segment.off)}
          >
            <Icon name={ICONS[option]} className="h-3.5 w-3.5" />
          </button>
        ))}
      </div>
    )
  }

  return (
    <div role="radiogroup" aria-label="Theme" className="border-t border-[var(--border)] pt-1">
      <p className="px-2.5 pb-0.5 pt-1 text-[10px] font-medium uppercase tracking-[0.08em] text-muted">Appearance</p>
      {THEMES.map((option) => {
        const on = theme === option
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => setTheme(option)}
            className={cx(
              'flex w-full items-center gap-2 rounded-xl px-2.5 py-1.5 text-left text-sm transition-colors hover:bg-hover',
              on ? 'text-foreground' : 'text-muted',
            )}
          >
            <Icon name={ICONS[option]} className="h-4 w-4 shrink-0" />
            <span className="flex-1">{THEME_LABELS[option]}</span>
            {on && <Icon name="check" className="h-3.5 w-3.5 shrink-0" />}
          </button>
        )
      })}
    </div>
  )
}
